import { useState } from 'react';
import { Loader } from '@/components/generic/Loader';
import { useModal } from '@/components/modals/Modal';
import { useToast } from '@/components/layout/Toast';
import RichTextEditorTextarea from '../../generic/form/RichTextEditorTextarea';
import { useTranslation } from '@/hooks/useTranslation';
import {
  UpdateNoteModal_NoteFragment,
  useUpdateNoteModal_NoteUpdateMutation,
} from '@/graphql/generated';

export function UpdateNoteModal({
  note,
}: {
  note: UpdateNoteModal_NoteFragment;
}) {
  const toast = useToast();
  const modal = useModal();
  const { t } = useTranslation();

  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content || '');

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateNoteMutation] = useUpdateNoteModal_NoteUpdateMutation();
  const updateNote = () => {
    setIsUpdating(true);
    updateNoteMutation({
      variables: {
        input: {
          id: note.id,
          title: title,
          content: content,
        },
      },
    })
      .then(() => {
        toast.openToastWithMessage(t('toast:notebook.note.update.success'));
        modal.closeModal();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsUpdating(false));
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="grow overflow-y-auto space-y-2">
        <div className="py-4 px-2 border-b border-gray-100">
          <input
            type="text"
            id="title"
            placeholder={t('notebook.note.update.title_placeholder')}
            className="form-input-text-inline sami-heading-medium px-4"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
          <RichTextEditorTextarea
            value={content}
            onChange={(value) => setContent(value)}
            placeholder={t('notebook.note.update.content_placeholder')}
            inlined={true}
          />
        </div>
      </div>

      <div className="flex gap-2 justify-between p-4 border-t border-gray-100">
        <button className="secondary purple" onClick={() => modal.closeModal()}>
          {t('global:cancel')}
        </button>
        <button
          className="primary purple"
          onClick={() => updateNote()}
          disabled={isUpdating || !title}
        >
          {isUpdating ? <Loader /> : t('global:save')}
        </button>
      </div>
    </div>
  );
}
